/**
 * Value-map editor dialog. column-mapping-ui spec, "Constant and value-map
 * editors": visibly flags any sampled source value not yet mapped and blocks
 * import until every sampled value has a target.
 */
import { AlertTriangle } from 'lucide-react';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { Badge } from '@ooh/ui/components/badge';
import { Input } from '@ooh/ui/components/input';
import { InfoHint } from '@ooh/ui/components/info-hint';
import { cn } from '@ooh/ui/lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ooh/ui/components/table';
import { formatTemplate } from '@ooh/ui/lib/utils';
import { EnumValueCombobox } from './enum-value-combobox';
import { EnumOptionsStatusBanner } from './enum-options-status-banner';
import {
  TransformDialogHeader,
  TRANSFORM_DIALOG_MAX_WIDTH,
} from './mapping-transforms-dialog-header';
import {
  TransformModeToggle,
  type TransformMode,
  type TransformModeToggleLabels,
} from './mapping-transforms-mode-toggle';

export interface ValueMapEntry {
  from: string;
  to: string;
  unmapped?: boolean;
}

export interface ValueMapEditorLabels extends TransformModeToggleLabels {
  dialogTitle: string;
  columnLabelPrefix: string;
  /** Accessible label for the dialog-level "(i)" info hint — see PatternEditorLabels'
   * matching field for why this is shared/duplicated across both transform dialogs, and
   * why it's optional (mockup-compat: TransformDialogHeader already skips the hint when
   * infoLabel/infoText are absent). */
  dialogInfoLabel?: string;
  /** Body copy for the dialog-level info hint. Optional for the same reason as dialogInfoLabel. */
  dialogInfoText?: string;
  fromColumnLabel: string;
  toColumnLabel: string;
  unmappedPlaceholder: string;
  /** Template with a `{value}` token, e.g. 'Import jest zablokowany, dopóki wartość "{value}" nie zostanie zmapowana.' */
  blockedWarningTemplate: string;
  doneButtonLabel: string;
  /** CommandInput placeholder for the enum-aware target-value picker (used when the mapped
   * field has a closed value set — see `enumOptions` below). Optional so apps/mockups'
   * pre-existing labels object (typed against this interface but not updated for the
   * enum-picker addition) keeps compiling — the enum-picker branch never renders in the
   * mockup since it never passes `enumOptions`. */
  valueSearchPlaceholder?: string;
  /** CommandEmpty text for the enum-aware target-value picker. Optional for the same
   * mockup-compat reason as valueSearchPlaceholder above. */
  valueNoResultsLabel?: string;
  /** Shown above the table while a query-backed enum field's options (e.g. media formats)
   * are still loading. Optional for the same mockup-compat reason as valueSearchPlaceholder
   * above — the static-enum fields never pass `enumOptionsLoading`. */
  enumOptionsLoadingLabel?: string;
  /** Shown above the table when a query-backed enum field's options failed to load. Optional
   * for the same reason as enumOptionsLoadingLabel above. */
  enumOptionsErrorLabel?: string;
}

export interface ValueMapEditorDialogProps {
  sourceColumnLabel: string;
  entries: ValueMapEntry[];
  labels: ValueMapEditorLabels;
  /** Optional — when provided, an unmapped row renders an editable input instead of a static badge. */
  onEntryChange?: (from: string, to: string) => void;
  /** Switches this column's transform mode back to pattern — reverse of the pattern editor's toggle. */
  onModeChange: (mode: TransformMode) => void;
  onClose: () => void;
  /** The mapped target field's closed value set (e.g. lighting) — when provided and
   * non-empty, every "Wartość w systemie" cell renders as a searchable dropdown restricted
   * to these values instead of free text, so an enum-backed field can't be mistyped. Omit
   * for open-ended target fields (notes, dimensions, etc). */
  enumOptions?: readonly string[];
  /** True while a query-backed enum field's options (e.g. media formats) are being fetched —
   * renders a loading note above the table instead of a silently empty combobox. */
  enumOptionsLoading?: boolean;
  /** True when a query-backed enum field's options failed to load — renders an inline error
   * above the table instead of silently falling back to free text with no explanation. */
  enumOptionsError?: boolean;
}

interface ValueMapTargetCellProps {
  entry: ValueMapEntry;
  labels: ValueMapEditorLabels;
  onEntryChange?: (from: string, to: string) => void;
  enumOptions?: readonly string[];
  enumOptionsLoading?: boolean;
  enumOptionsError?: boolean;
}

/** The "Wartość w systemie" cell content for one value-map row — extracted out of the
 * `entries.map()` callback below to keep that callback's complexity low; all the
 * enum/loading/error/free-text branching for a single row lives here instead. */
function ValueMapTargetCell({
  entry,
  labels,
  onEntryChange,
  enumOptions,
  enumOptionsLoading,
  enumOptionsError,
}: ValueMapTargetCellProps) {
  if (!onEntryChange) {
    // Read-only rendering — see the "Always editable, even once resolved" comment on the
    // editable branches below for why the entry.unmapped check only applies here.
    return entry.unmapped ? (
      <Badge variant="warning">{labels.unmappedPlaceholder}</Badge>
    ) : (
      <span>{entry.to}</span>
    );
  }

  if (enumOptionsLoading || enumOptionsError) {
    // Query-backed enum field (e.g. 'format') still loading/errored — never fall back to an
    // editable free-text input here, which would let the operator type an arbitrary,
    // unvalidated value while the real closed value set isn't known yet. Mirrors
    // ConstantEditorDialog's loading/error special-casing (mapping-transforms-constant-dialog.tsx).
    return (
      <Input
        value={entry.to}
        disabled
        placeholder={
          enumOptionsError ? labels.enumOptionsErrorLabel : labels.enumOptionsLoadingLabel
        }
        data-testid={`value-map-entry-${entry.from}`}
        className="h-8"
      />
    );
  }

  if (enumOptions && enumOptions.length > 0) {
    return (
      <EnumValueCombobox
        value={entry.to}
        options={enumOptions}
        onChange={(value) => onEntryChange(entry.from, value)}
        labels={{
          searchPlaceholder: labels.valueSearchPlaceholder ?? '',
          noResultsLabel: labels.valueNoResultsLabel ?? '',
          placeholderLabel: labels.unmappedPlaceholder,
        }}
        testId={`value-map-entry-${entry.from}`}
      />
    );
  }

  // Always editable, even once resolved — reopening the dialog after a value was already
  // assigned (pre-filled by a suggestion or a prior edit) must not permanently lock the cell
  // into a static, unclickable span (CU-86can7jwy bug fix: "Value-map dialog entries become
  // permanently uneditable once resolved").
  return (
    <Input
      value={entry.to}
      placeholder={labels.unmappedPlaceholder}
      onChange={(e) => onEntryChange(entry.from, e.target.value)}
      data-testid={`value-map-entry-${entry.from}`}
      className="h-8"
    />
  );
}

export function ValueMapEditorDialog({
  sourceColumnLabel,
  entries,
  labels,
  onEntryChange,
  onModeChange,
  onClose,
  enumOptions,
  enumOptionsLoading,
  enumOptionsError,
}: ValueMapEditorDialogProps) {
  const firstUnmapped = entries.find((e) => e.unmapped);
  const blockedWarningText = firstUnmapped
    ? formatTemplate(labels.blockedWarningTemplate, { value: firstUnmapped.from })
    : null;

  return (
    <ModalDialog
      header={
        <TransformDialogHeader
          title={labels.dialogTitle}
          columnLabelPrefix={labels.columnLabelPrefix}
          sourceColumnLabel={sourceColumnLabel}
          infoLabel={labels.dialogInfoLabel}
          infoText={labels.dialogInfoText}
        />
      }
      onClose={onClose}
      maxWidth={TRANSFORM_DIALOG_MAX_WIDTH}
      testId="value-map-editor-dialog"
      footer={
        <Button type="button" onClick={onClose} data-testid="value-map-editor-done">
          {labels.doneButtonLabel}
        </Button>
      }
    >
      <div className="space-y-3" data-testid="value-map-editor">
        <div className="flex items-center gap-1.5">
          <TransformModeToggle mode="valueMap" labels={labels} onModeChange={onModeChange} />
          <InfoHint label={labels.modeToggleInfoLabel}>{labels.modeToggleInfoText}</InfoHint>
        </div>
        {enumOptionsError ? (
          <EnumOptionsStatusBanner
            variant="error"
            message={labels.enumOptionsErrorLabel ?? ''}
            testId="value-map-enum-error"
          />
        ) : enumOptionsLoading ? (
          <EnumOptionsStatusBanner
            variant="loading"
            message={labels.enumOptionsLoadingLabel ?? ''}
            testId="value-map-enum-loading"
          />
        ) : null}
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead caps={false} className="px-3 py-2">
                  {labels.fromColumnLabel}
                </TableHead>
                <TableHead caps={false} className="px-3 py-2">
                  {labels.toColumnLabel}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <TableRow
                  key={entry.from}
                  className={cn(
                    // DS-BYPASS: no semantic row-highlight token for an unmapped value-map row; Badge doesn't apply to a row background.
                    entry.unmapped && 'bg-amber-50/50',
                  )}
                >
                  <TableCell className="px-3 py-2 font-mono text-xs">{entry.from}</TableCell>
                  <TableCell className="px-3 py-2">
                    <ValueMapTargetCell
                      entry={entry}
                      labels={labels}
                      onEntryChange={onEntryChange}
                      enumOptions={enumOptions}
                      enumOptionsLoading={enumOptionsLoading}
                      enumOptionsError={enumOptionsError}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {blockedWarningText ? (
          // DS-BYPASS: blocking-warning alert box has no semantic banner token/primitive yet; Badge doesn't fit a bordered multi-line alert.
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" strokeWidth={1.8} />
            <p className="text-xs text-amber-800">{blockedWarningText}</p>
          </div>
        ) : null}
      </div>
    </ModalDialog>
  );
}
